import { Injectable } from '@angular/core';
import {
    Observable,
    defer,
    from,
    timer,
    EMPTY,
    Subscription,
} from 'rxjs';
import { expand, switchMap, takeUntil } from 'rxjs/operators';

/**
 * Options for configuring the polling behavior.
 */
export interface PollOptions<T> {
    /**
     * Function to determine if polling should exit early.
     */
    earlyExitFn?: (data: T) => boolean;

    /**
     * Time in milliseconds between polling attempts.
     * @default 1000
     */
    pollInterval?: number;

    /**
     * Message to emit when the global timeout is reached.
     * @default 'Polling timed out.'
     */
    timeoutMessage?: string;

    /**
     * Global timeout in milliseconds for the polling process.
     * @default 60000
     */
    waitLimit?: number;
}

@Injectable({ providedIn: 'root' })
export class PollingService {
    /**
     * Starts polling by invoking the provided fetch function until:
     * - earlyExitFn returns true, or
     * - the global timeout is reached.
     *
     * Emits each response and completes or errors accordingly.
     *
     * @param fetchFn Function returning a Promise or Observable of the data to poll.
     * @param options Optional polling configuration.
     * @returns Observable emitting each poll result until completion or error.
     *
     * @example
     * ```ts
     * // Start polling every 2 seconds, stop after 30 seconds or when response.done === true
     * const polling$ = this.pollingService.poll<MyResponse>(
     *   () => this.http.get<MyResponse>('api/status'),
     *   {
     *     pollInterval: 2000,
     *     waitLimit: 30000,
     *     earlyExitFn: res => res.done,
     *   }
     * );
     *
     * const subscription = polling$.subscribe({
     *   next: res => console.log('Polling response:', res),
     *   error: err => console.error('Polling failed:', err.message),
     *   complete: () => console.log('Polling complete')
     * });
     *
     * // To cancel active polling subscription:
     * this.pollingService.cancelPolling(subscription);
     * ```
     */
    poll<T>(
        fetchFn: () => Promise<T> | Observable<T>,
        options: PollOptions<T> = {}
    ): Observable<T> {
        const {
            earlyExitFn = () => false,
            pollInterval = 1000,
            timeoutMessage = 'Polling timed out.',
            waitLimit = 60000
        } = options;

        const createFetch$ = () => defer(() => {
            const result = fetchFn();
            return result instanceof Promise ? from(result) : result;
        });

        return new Observable<T>((subscriber) => {
            let timedOut = true;

            const polling$ = createFetch$().pipe(
                expand((response) => {
                    if (earlyExitFn(response)) {
                        timedOut = false;

                        return EMPTY;
                    }

                    return timer(pollInterval).pipe(
                        switchMap(() => createFetch$())
                    );
                }),
                takeUntil(timer(waitLimit))
            );

            const subscription = polling$.subscribe({
                next: (val) => subscriber.next(val),
                error: (err) => subscriber.error(err),
                complete: () => {
                    if (timedOut) {
                        subscriber.error(new Error(timeoutMessage));
                    } else {
                        subscriber.complete();
                    }
                },
            });

            return () => {
                subscription.unsubscribe();
            };
        });
    }

    /**
     * Cancels an active polling subscription.
     * The subscription must be passed in by the caller.
     *
     * @param subscription The polling subscription to cancel.
     */
    cancelPolling(subscription: Subscription): void {
        if (!subscription.closed) {
            subscription.unsubscribe();
        }
    }
}
